// RequestFilesField (#1445) — REUSABLE request-attachment field.
//
// @aiPurpose self-contained upload + list + download + remove control for
//   Request file attachments. NOT mounted in any form here — #1446/#1448
//   wire it into the draft editor / detail drawer. Until a `requestId` is
//   persisted, the control is inert: it makes NO upload-url request, NO
//   PUT, and NO metadata create, and shows a "save the draft first" hint.
// @aiSecurity images are re-encoded to JPEG via `compressImage` before the
//   PUT; documents upload AS-IS (never run through the image canvas). The
//   source MIME is gated against `REQUEST_FILE_ALLOWED_MIME` before any
//   network call — the backend re-validates both type and size, so this is
//   a UX shortcut, not the source of truth.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  REQUEST_FILE_ALLOWED_MIME,
  REQUEST_FILE_MAX_BYTES,
  REQUEST_FILE_MAX_PER_REQUEST,
  type RequestFile,
  type RequestFileMime,
} from '@magimp/shared';
import { Button, EmptyState, Icon, IconButton } from '../../../ui/index.js';
import { formatDateTime, formatSize } from '../../../i18n/index.js';
import {
  createRequestFile,
  deleteRequestFile,
  listRequestFiles,
  requestFileDownloadUrl,
  requestUploadUrl,
} from '../../../api/requests/index.js';
import { compressImage } from '../photoCompress.js';

export interface RequestFilesFieldProps {
  /** Persisted request id, or null/absent for an unsaved draft. */
  requestId: string | null;
}

const ALLOWED = new Set<string>(REQUEST_FILE_ALLOWED_MIME);

function isAllowedMime(type: string): type is RequestFileMime {
  return ALLOWED.has(type);
}

function isImage(contentType: string): boolean {
  return contentType.startsWith('image/');
}

export function RequestFilesField({ requestId }: RequestFilesFieldProps) {
  const { t } = useTranslation(['requests', 'common']);
  const [files, setFiles] = useState<RequestFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!requestId) {
      setFiles([]);
      return;
    }
    let cancelled = false;
    void listRequestFiles(requestId).then(
      (rows) => {
        if (!cancelled) setFiles(rows);
      },
      () => {
        if (!cancelled) setError(t('requests:files.errors.listFailed'));
      },
    );
    return () => {
      cancelled = true;
    };
  }, [requestId, t]);

  const capReached = files.length >= REQUEST_FILE_MAX_PER_REQUEST;
  // INVARIANT: with no persisted id the control is fully inert; the cap is
  //   irrelevant because no upload can fire.
  const addDisabled = !requestId || uploading || capReached;

  const onSelectFile = useCallback(
    async (file: File) => {
      // Guard again at the top: a programmatic call must never upload
      // against a null request id.
      if (!requestId) return;
      if (!isAllowedMime(file.type)) {
        setError(t('requests:files.errors.wrongType'));
        return;
      }
      setError(null);
      setUploading(true);
      try {
        // Images: re-encode to JPEG (cap applies to the COMPRESSED blob).
        // Documents: upload the original bytes AS-IS.
        let body: Blob;
        let contentType: RequestFileMime;
        if (isImage(file.type)) {
          body = await compressImage(file);
          contentType = 'image/jpeg';
        } else {
          body = file;
          contentType = file.type;
        }
        if (body.size > REQUEST_FILE_MAX_BYTES) {
          setError(t('requests:files.errors.tooLarge'));
          return;
        }
        const signed = await requestUploadUrl({
          scope: 'request-attachment',
          contentType,
          contentLength: body.size,
        });
        const put = await fetch(signed.uploadUrl, {
          method: 'PUT',
          body,
          headers: { 'content-type': contentType },
        });
        if (!put.ok) throw new Error(`upload failed: ${put.status}`);
        const created = await createRequestFile(requestId, {
          objectKey: signed.objectKey,
          fileName: file.name,
          contentType,
          contentLength: body.size,
        });
        setFiles((prev) => [...prev, created]);
      } catch {
        setError(t('requests:files.errors.uploadFailed'));
      } finally {
        setUploading(false);
      }
    },
    [requestId, t],
  );

  const onRemove = useCallback(
    async (fileId: string) => {
      if (!requestId) return;
      try {
        await deleteRequestFile(requestId, fileId);
        setFiles((prev) => prev.filter((f) => f.id !== fileId));
      } catch {
        setError(t('requests:files.errors.removeFailed'));
      }
    },
    [requestId, t],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: 'none' }}
        disabled={addDisabled}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void onSelectFile(file);
          e.target.value = '';
        }}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <Button
          kind="secondary"
          size="sm"
          icon="upload-cloud"
          disabled={addDisabled}
          onClick={() => fileInputRef.current?.click()}
          data-testid="request-files-add"
        >
          {uploading ? t('requests:files.uploading') : t('requests:files.add')}
        </Button>
        <span style={{ fontSize: 11, color: 'var(--fg-3)' }}>
          {t('requests:files.usage', {
            count: files.length,
            max: REQUEST_FILE_MAX_PER_REQUEST,
          })}
        </span>
      </div>

      {!requestId && (
        <span style={{ fontSize: 12, color: 'var(--fg-3)' }} data-testid="request-files-save-first">
          {t('requests:files.saveFirst')}
        </span>
      )}

      {requestId && capReached && (
        <span style={{ fontSize: 11, color: 'var(--fg-3)' }}>{t('requests:files.capReached')}</span>
      )}

      {error && (
        <span
          role="alert"
          style={{
            fontSize: 11,
            color: 'var(--status-error-fg)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <Icon name="alert-triangle" size={12} />
          {error}
        </span>
      )}

      {requestId && files.length === 0 && !error && (
        <EmptyState icon="paperclip" title={t('requests:files.empty')} />
      )}

      {requestId && files.length > 0 && (
        <ul
          data-testid="request-files-list"
          style={{
            margin: 0,
            padding: 0,
            listStyle: 'none',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
          }}
        >
          {files.map((file) => (
            <RequestFileRow
              key={file.id}
              requestId={requestId}
              file={file}
              onRemove={() => void onRemove(file.id)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function RequestFileRow({
  requestId,
  file,
  onRemove,
}: {
  requestId: string;
  file: RequestFile;
  onRemove: () => void;
}) {
  const { t } = useTranslation(['requests', 'common']);
  const [thumbUrl, setThumbUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const image = isImage(file.contentType);

  // Image rows resolve a presigned URL once for inline preview. Document
  // rows resolve on demand (download click) only.
  useEffect(() => {
    if (!image) return;
    let cancelled = false;
    void requestFileDownloadUrl(requestId, file.id).then(
      (res) => {
        if (!cancelled) setThumbUrl(res.downloadUrl);
      },
      () => undefined,
    );
    return () => {
      cancelled = true;
    };
  }, [image, requestId, file.id]);

  async function onDownload() {
    setBusy(true);
    try {
      const res = await requestFileDownloadUrl(requestId, file.id);
      window.open(res.downloadUrl, '_blank', 'noopener,noreferrer');
    } catch {
      // Surfacing belongs to the parent in #1446; here a failed download
      // simply leaves the row untouched.
    } finally {
      setBusy(false);
    }
  }

  return (
    <li
      data-testid="request-file-row"
      data-file-id={file.id}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 8px',
        borderRadius: 'var(--r-2)',
        border: '1px solid var(--border-1)',
        background: 'var(--bg-1)',
      }}
    >
      {image && thumbUrl ? (
        <img
          src={thumbUrl}
          alt={file.fileName}
          style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 'var(--r-1)' }}
        />
      ) : (
        <Icon name={image ? 'image' : 'file-text'} size={16} />
      )}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span
          title={file.fileName}
          style={{
            fontSize: 13,
            color: 'var(--fg-1)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {file.fileName}
        </span>
        <span style={{ fontSize: 11, color: 'var(--fg-3)' }}>
          {formatSize(file.contentLength)} · {formatDateTime(file.createdAt)}
        </span>
      </div>
      <Button
        kind="ghost"
        size="sm"
        icon="download"
        aria-label={t('requests:files.download')}
        disabled={busy}
        onClick={() => void onDownload()}
        data-testid="request-file-download"
      />
      <IconButton
        icon="trash-2"
        size={24}
        label={t('requests:files.remove')}
        onClick={onRemove}
        data-testid="request-file-remove"
      />
    </li>
  );
}
